import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, Sparkles, Check, X, ArrowLeft, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminApi';

const Row = ({ label, children }) => (
  <div className="py-2 border-b border-[#f0f0f0] grid grid-cols-3 gap-3 text-sm">
    <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">{label}</div>
    <div className="col-span-2">{children || <span className="text-neutral-300">—</span>}</div>
  </div>
);

const consentBadge = (c) => c?.granted
  ? <span className="text-[#1a7a3f]">✓ Granted {c.version ? `(v${c.version})` : ''} {c.at ? `· ${c.at.slice(0, 10)}` : ''}</span>
  : <span className="text-neutral-400">Not granted</span>;

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [bio, setBio] = useState('');
  const [glam, setGlam] = useState('');
  const [identityStatus, setIdentityStatus] = useState('pending');

  const load = () => {
    setLoading(true);
    adminApi.getApplication(id)
      .then(d => { setA(d); setBio(d.admin_bio || d.generated_bio || ''); setGlam(d.glam_score ?? ''); setIdentityStatus(d.identity_status || 'pending'); })
      .catch(() => toast.error('Not found'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const genBio = async () => {
    setBusy('bio');
    try { const r = await adminApi.generateBio(id); setBio(r.bio); toast.success('Bio generated'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Bio generation failed'); }
    finally { setBusy(''); }
  };

  const saveReview = async () => {
    setBusy('save');
    try {
      await adminApi.patchApplication(id, {
        admin_bio: bio,
        glam_score: glam === '' ? null : Number(glam),
        identity_status: identityStatus,
      });
      toast.success('Saved');
      load();
    } catch (e) { toast.error('Save failed'); }
    finally { setBusy(''); }
  };

  const approve = async (publish) => {
    setBusy('approve');
    try {
      await adminApi.patchApplication(id, { admin_bio: bio, glam_score: glam === '' ? null : Number(glam), identity_status: identityStatus });
      const r = await adminApi.approveApplication(id, publish);
      toast.success(publish ? 'Approved & published' : 'Approved (draft)');
      nav('/admin/applications');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(''); }
  };

  const reject = async () => {
    const reason = window.prompt('Reason for rejection (optional):') ?? '';
    setBusy('reject');
    try { await adminApi.rejectApplication(id, reason); toast.success('Rejected'); nav('/admin/applications'); }
    catch (e) { toast.error('Reject failed'); }
    finally { setBusy(''); }
  };

  if (loading) return <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!a) return <div className="text-sm text-neutral-500">Application not found. <Link to="/admin/applications" className="text-[#eb5381]">Back</Link></div>;

  return (
    <div data-testid="admin-application-detail" className="max-w-5xl">
      <Link to="/admin/applications" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-neutral-500 hover:text-[#eb5381]"><ArrowLeft className="w-4 h-4" /> Back to queue</Link>
      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif text-3xl md:text-4xl">{a.name}</h1>
        <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-neutral-100">{a.status}{a.linked_slug ? ` · /artists/${a.linked_slug}` : ''}</span>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-8">
          <section className="bg-white border border-[#eaeaea] p-5">
            <h2 className="font-serif text-xl mb-3">Details</h2>
            <Row label="Email">{a.email}</Row>
            <Row label="Phone">{a.phone}</Row>
            <Row label="City">{a.city}</Row>
            <Row label="Instagram">{a.instagram}</Row>
            <Row label="Experience">{a.years_experience != null ? `${a.years_experience} yrs` : ''}</Row>
            <Row label="Starting price">{a.starting_price != null ? `₹${a.starting_price}` : ''}</Row>
            <Row label="Occasions">{(a.occasions || []).join(', ')}</Row>
            <Row label="Techniques">{(a.techniques || []).join(', ')}</Row>
            <Row label="Product kit">{a.product_kit}</Row>
            <Row label="Pricing (₹)">
              {a.category_prices && Object.keys(a.category_prices).length
                ? <div className="space-y-0.5">{Object.entries(a.category_prices).map(([occ, v]) => (
                    <div key={occ} className="text-xs"><span className="text-neutral-500">{occ}:</span> {Object.entries(v).map(([t, p]) => `${t} ₹${p}`).join(' · ')}</div>
                  ))}</div>
                : null}
            </Row>
            <Row label="Services">{[a.home_service && 'Home', a.studio_service && 'Studio', a.trial_available && 'Trials', a.hairstyling && 'Hair'].filter(Boolean).join(' · ')}</Row>
            <Row label="Experience note">{a.experience_note}</Row>
            {a.custom_fields && Object.keys(a.custom_fields).length > 0 && (
              <Row label="Custom answers">
                <div className="space-y-0.5">{Object.entries(a.custom_fields).map(([k, v]) => (
                  <div key={k} className="text-xs"><span className="text-neutral-400">{k}:</span> {String(v)}</div>
                ))}</div>
              </Row>
            )}
          </section>

          <section className="bg-white border border-[#eaeaea] p-5">
            <h2 className="font-serif text-xl mb-3">Certifications</h2>
            {(a.certifications || []).length === 0 ? <p className="text-sm text-neutral-400">None</p> : (
              <ul className="space-y-2">
                {a.certifications.map((c, i) => (
                  <li key={i} className="text-sm border border-[#f0f0f0] p-3">
                    <div className="font-medium">{c.other_institution || c.institution}{c.course ? ` — ${c.course}` : ''}{c.year ? ` (${c.year})` : ''}</div>
                    {c.details && <div className="text-neutral-500">{c.details}</div>}
                    {c.certificate_url && <a href={c.certificate_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#eb5381] text-xs mt-1">View certificate <ExternalLink className="w-3 h-3" /></a>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bg-white border border-[#eaeaea] p-5">
            <h2 className="font-serif text-xl mb-3">Portfolio ({(a.portfolio || []).length})</h2>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {(a.portfolio || []).map((src, i) => (
                <a key={i} href={src} target="_blank" rel="noreferrer" className="aspect-square bg-neutral-100 block overflow-hidden">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          </section>

          <section className="bg-white border border-[#eaeaea] p-5">
            <h2 className="font-serif text-xl mb-3">Consent & Verification</h2>
            <Row label="Commission">{consentBadge(a.consent?.commission)}</Row>
            <Row label="Marketing">{a.consent?.marketing?.withdrawn_at ? <span className="text-[#b42318]">Withdrawn {a.consent.marketing.withdrawn_at.slice(0,10)}</span> : consentBadge(a.consent?.marketing)}</Row>
            <Row label="Identity doc">
              {a.identity?.doc_url
                ? <a href={a.identity.doc_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#eb5381]">{a.identity.doc_type || 'Document'} <Download className="w-3 h-3" /></a>
                : 'Not provided'}
            </Row>
            <Row label="Payment">
              <span className={a.payment_status === 'paid' ? 'text-[#1a7a3f]' : 'text-neutral-400'}>{a.payment_status === 'paid' ? '✓ Paid' : 'Unpaid'}</span>
              {a.payment_screenshot_url && <a href={a.payment_screenshot_url} target="_blank" rel="noreferrer" className="text-[#eb5381] ml-3">View screenshot</a>}
            </Row>
          </section>
        </div>

        {/* Right: review actions */}
        <div className="space-y-6">
          <section className="bg-white border border-[#eaeaea] p-5 lg:sticky lg:top-6">
            <h2 className="font-serif text-xl mb-4">Review</h2>

            <label className="block mb-4">
              <span className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">Glam Score (0–5)</span>
              <input data-testid="review-glam" type="number" min="0" max="5" step="0.1" value={glam} onChange={e => setGlam(e.target.value)} className="mt-1 w-full border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2 text-sm" />
            </label>

            <label className="block mb-4">
              <span className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">Identity status</span>
              <select data-testid="review-identity" value={identityStatus} onChange={e => setIdentityStatus(e.target.value)} className="mt-1 w-full border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2 text-sm">
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </label>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-[0.15em] text-neutral-500">Artist bio</span>
                <button onClick={genBio} disabled={busy === 'bio'} data-testid="generate-bio-btn" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#eb5381]">
                  {busy === 'bio' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {a.generated_bio ? 'Regenerate' : 'Generate'}
                </button>
              </div>
              <textarea data-testid="review-bio" rows={7} value={bio} onChange={e => setBio(e.target.value)} className="w-full border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2 text-sm" placeholder="Generate with AI or write manually…" />
            </div>

            <button onClick={saveReview} disabled={busy === 'save'} data-testid="save-review-btn" className="w-full mb-2 border border-neutral-800 text-neutral-800 hover:bg-neutral-800 hover:text-white px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors">Save changes</button>
            <button onClick={() => approve(true)} disabled={busy === 'approve'} data-testid="approve-publish-btn" className="w-full mb-2 inline-flex items-center justify-center gap-2 bg-[#eb5381] hover:bg-[#d64570] text-white px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors">{busy === 'approve' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve & publish</button>
            <button onClick={() => approve(false)} disabled={busy === 'approve'} data-testid="approve-draft-btn" className="w-full mb-2 border border-[#eaeaea] hover:border-[#eb5381] px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors">Approve as draft</button>
            <button onClick={reject} disabled={busy === 'reject'} data-testid="reject-btn" className="w-full inline-flex items-center justify-center gap-2 text-[#b42318] border border-[#f3c6c6] hover:bg-[#fde0e0] px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors"><X className="w-3 h-3" /> Reject</button>
          </section>
        </div>
      </div>
    </div>
  );
}
